import * as React from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  Flex,
  IconButton,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Tooltip,
  Text,
  TableContainer,
  useColorModeValue,
  Button,
  Box,
  HStack,
  FormLabel,
} from "@chakra-ui/react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import {
  Table as TableReactTable,
  useReactTable,
  flexRender,
  getCoreRowModel,
  ColumnDef,
  SortingState,
  getSortedRowModel,
  getPaginationRowModel,
  ColumnFiltersState,
  getFilteredRowModel,
  Column,
} from "@tanstack/react-table";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa6";
import { useTranslation } from "react-i18next";

export type DataTableProps<Data extends object> = {
  data: Data[];
  columns: ColumnDef<Data, any>[];
};

/**
 * DataTable component renders a customizable table with sorting, filtering, and pagination functionalities.
 * 
 * @template Data - The type of data being displayed in the table.
 * 
 * @param {DataTableProps<Data>} props - The properties for the DataTable component.
 * @param {Data[]} props.data - The data to be displayed in the table.
 * @param {ColumnDef<Data>[]} props.columns - The column definitions for the table.
 * 
 * @returns {JSX.Element} The rendered DataTable component.
 * 
 * @component
 * 
 * @example
 * ```tsx
 * const data = [
 *   { id: 1, name: 'John Doe', age: 28 },
 *   { id: 2, name: 'Jane Doe', age: 32 },
 * ];
 * 
 * const columns = [
 *   {
 *     accessorKey: 'name',
 *     header: 'Name',
 *     cell: info => info.getValue(),
 *   },
 *   {
 *     accessorKey: 'age',
 *     header: 'Age',
 *     cell: info => info.getValue(),
 *     meta: { isNumeric: true },
 *   },
 * ];
 * 
 * <DataTable data={data} columns={columns} />
 * ```
 */
export default function DataTable<Data extends object>({
  data,
  columns,
}: DataTableProps<Data>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const table = useReactTable({
    columns,
    data: data,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      columnFilters,
      sorting,
    },
  });
  const bg = useColorModeValue("white", "gray.800");

  return (
    <TableContainer bg={bg}>
      <Table variant="striped" size="sm">
        <Thead >
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id} pt={0}>
              {headerGroup.headers.map((header) => {
                const meta: any = header.column.columnDef.meta;
                return (
                  <Th key={header.id} isNumeric={meta?.isNumeric}>
                    <Box width={"100%"}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}

                      <chakra.button
                        pl="4"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {header.column.getIsSorted() ? (
                          header.column.getIsSorted() === "desc" ? (
                            <FaSortDown aria-label="sorted descending" />
                          ) : (
                            <FaSortUp aria-label="sorted ascending" />
                          )
                        ) : (
                          header.column.getCanSort() && (
                            <FaSort aria-label="not sorted" />
                          )
                        )}
                      </chakra.button>
                    </Box>

                    <Box minH={"2.5em"}>
                      {header.column.getCanFilter() ? (
                        <div>
                          <Filter column={header.column} table={table} />
                        </div>
                      ) : null}
                    </Box>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id}>
              {row.getVisibleCells().map((cell) => {
                const meta: any = cell.column.columnDef.meta;
                return (
                  <Td key={cell.id} isNumeric={meta?.isNumeric}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>

      {
        (
          <Flex justifyContent="space-between" m={4} alignItems="center" as="nav">
            <Flex>
              <Tooltip label={`Vai alla prima pagina`}>
                <IconButton
                  onClick={() => table.setPageIndex(0)}
                  isDisabled={!table.getCanPreviousPage()}
                  icon={<ArrowLeftIcon h={3} w={3} />}
                  mr={4}
                  aria-label="button"
                  size={"sm"}
                />
              </Tooltip>
              <Tooltip label={`Vai alla pagina precedente`}>
                <IconButton
                  onClick={() => table.previousPage()}
                  isDisabled={!table.getCanPreviousPage()}
                  icon={<ChevronLeftIcon h={6} w={6} />}
                  aria-label="button"
                  size={"sm"}

                />
              </Tooltip>
            </Flex>

            <Flex alignItems="center">
              <Text mr={8}>
                Pagina {`${table.getState().pagination.pageIndex + 1}`} di {`${table.getPageCount()}`}
              </Text>
              <Text as="label" htmlFor="move-to-page">{`Vai alla pagina`}</Text>
              <NumberInput
                id="move-to-page"
                ml={2}
                mr={8}
                w={28}
                min={1}
                max={table.getPageOptions()?.length}
                onChange={(value) => {
                  const page = value ? Number(value) - 1 : 0;
                  table.setPageIndex(page);
                }}
                defaultValue={table.getState().pagination.pageIndex + 1}
                size={"sm"}

              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>

              <Select
                aria-label="Seleziona il numero di elementi visualizzati per pagina"
                w={56}
                value={table.getState().pagination.pageSize}
                onChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                }}
                size={"sm"}
              >
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {`Elementi per pagina:`} {pageSize}
                  </option>
                ))}
              </Select>
            </Flex>

            <Flex>
              <Tooltip label={`Vai alla pagina successiva`}>
                <IconButton
                  onClick={() => table.nextPage()}
                  isDisabled={!table.getCanNextPage()}
                  icon={<ChevronRightIcon h={6} w={6} />}
                  aria-label="button"
                  size={"sm"}
                />
              </Tooltip>
              <Tooltip label={`Vai all'ultima pagina`}>
                <IconButton

                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  isDisabled={!table.getCanNextPage()}
                  icon={<ArrowRightIcon h={3} w={3} />}
                  ml={4}
                  aria-label="button"
                  size={"sm"}

                />
              </Tooltip>
            </Flex>
          </Flex>
        )


      }


    </TableContainer>
  );
}

function Filter({
  column,
  table,
}: {
  column: Column<any, unknown>;
  table: TableReactTable<any>;
}) {

  const columnFilterValue = column.getFilterValue();
  const filterMeta: any = column.columnDef.meta;



  return filterMeta?.filterType == 'select' ?
    <>
      <Select size={"xs"}
        aria-label={`Seleziona un valore per filtrare la colonna ${column.columnDef.header}`}
        value={(columnFilterValue ?? "") as string}
        onChange={(e) => column.setFilterValue(e.target.value)}
      >
        <option value="">{filterMeta?.placeholder}</option>
        {filterMeta.selectOptions.map((option: any) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
    </> :

    (
      <DebouncedInput
        aria-label={`Inserisci un valore per filtrare la colonna ${column.columnDef.header}`}
        type="text"
        value={(columnFilterValue ?? "") as string}
        onChange={(value) => column.setFilterValue(value)}
        placeholder={`Cerca...`}
        className="w-36 border shadow rounded"
        list={column.id + "list"}
      />
    );
}

// A debounced input react component
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <chakra.input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}
